import React, { useState, useEffect } from 'react';
import { X, Camera, Send, QrCode, Phone, Globe, Instagram, Facebook, Twitter } from 'lucide-react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const VisitSummaryModal = ({ isOpen, onClose }) => {
  const { currentUser, userProfile } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    phone: '',
    website: '',
    instagram: '',
    facebook: '',
    twitter: '',
  });
  const [formData, setFormData] = useState({
    petName: '',
    dateStayed: '',
    message: '',
    additionalNotes: '',
    photos: [],
  });

  // Fetch user's business info
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      if (!currentUser) return;
      
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setBusinessInfo({
            businessName: data.businessName || userProfile?.displayName || 'Your Pet Care Business',
            phone: data.phone || '',
            website: data.website || '',
            instagram: data.instagram || '',
            facebook: data.facebook || '',
            twitter: data.twitter || '',
          });
        }
      } catch (error) {
        console.error('Error fetching business info:', error);
      }
    };

    fetchBusinessInfo();
  }, [currentUser, userProfile]);

  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      if (!currentUser) return;
      
      try {
        const clientsRef = collection(db, 'clients');
        const q = query(clientsRef, where('sitterId', '==', currentUser.uid));
        const snapshot = await getDocs(q);
        const clientsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClients(clientsData);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    fetchClients();
  }, [currentUser]);

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + formData.photos.length > 3) {
      alert('Maximum 3 photos allowed');
      return;
    }

    // Create preview URLs for the photos
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setFormData(prev => ({
      ...prev,
      photos: [...prev.photos, ...newPhotos]
    }));
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const client = clients.find(c => c.id === selectedClient);
    if (!client) {
      alert('Please select a client');
      return;
    }

    // Create the visit summary message
    const visitMessage = `
Pet Visit Summary for ${formData.petName}
Date: ${formData.dateStayed}

${formData.message}

${formData.additionalNotes ? `Additional Notes: ${formData.additionalNotes}` : ''}

Thanks for trusting us with your pet!
- ${businessInfo.businessName}
    `.trim();

    // Show options to send
    const sendMethod = confirm(`Send visit summary to ${client.name}?\n\nOK = Email\nCancel = Choose manually`);
    
    if (sendMethod) {
      // Send via Email
      if (client.email) {
        const subject = encodeURIComponent(`Visit Summary for ${formData.petName} - ${businessInfo.businessName}`);
        const body = encodeURIComponent(visitMessage);
        window.location.href = `mailto:${client.email}?subject=${subject}&body=${body}`;
      } else {
        alert('Client has no email address. Please add it in the Clients page.');
      }
    } else {
      // Show SMS option
      if (client.phone) {
        const useSMS = confirm('Send via SMS/Text?');
        if (useSMS) {
          const phoneNumber = client.phone.replace(/\D/g, '');
          const messageBody = encodeURIComponent(visitMessage);
          const separator = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? '&' : '?';
          window.location.href = `sms:${phoneNumber}${separator}body=${messageBody}`;
        }
      } else {
        alert('Client has no phone number. Please add it in the Clients page.');
      }
    }
    
    // Reset form
    setFormData({
      petName: '',
      dateStayed: '',
      message: '',
      additionalNotes: '',
      photos: [],
    });
    setSelectedClient('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto scrollbar-thin">
        {/* Header with paw prints */}
        <div className="bg-gradient-to-r from-pastel-pink via-pastel-blue to-pastel-purple p-6 rounded-t-2xl relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-xl bg-white/80 hover:bg-white transition-colors"
          >
            <X className="h-5 w-5 text-brand-brown" />
          </button>
          
          <div className="text-center">
            <div className="flex items-center justify-center space-x-4 mb-2">
              <span className="text-4xl">🐾</span>
              <h2 className="text-2xl font-bold text-brand-brown">Thank You!</h2>
              <span className="text-4xl">🐾</span>
            </div>
            <p className="text-brand-brown/70">Leave After Visit Summary</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Client Selection */}
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1">
              Send to Client
            </label>
            <select
              required
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="input"
            >
              <option value="">Select a client</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name} - {client.email}
                </option>
              ))}
            </select>
          </div>

          {/* Pet Name */}
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1">
              Pet Name(s)
            </label>
            <input
              type="text"
              required
              value={formData.petName}
              onChange={(e) => setFormData(prev => ({ ...prev, petName: e.target.value }))}
              placeholder="Buddy, Max, Luna..."
              className="input"
            />
          </div>

          {/* Date Stayed */}
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1">
              Date(s) Stayed
            </label>
            <input
              type="text"
              required
              value={formData.dateStayed}
              onChange={(e) => setFormData(prev => ({ ...prev, dateStayed: e.target.value }))}
              placeholder="Dec 8, 2024 or Dec 8-10, 2024"
              className="input"
            />
          </div>

          {/* Message about stay */}
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1">
              Message to Pet Parent (How the stay went)
            </label>
            <textarea
              required
              rows="4"
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Tell the pet parent how their furry friend did during the stay..."
              className="textarea"
            />
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-1">
              Additional Notes
            </label>
            <textarea
              rows="3"
              value={formData.additionalNotes}
              onChange={(e) => setFormData(prev => ({ ...prev, additionalNotes: e.target.value }))}
              placeholder="Any special moments, funny stories, or important information..."
              className="textarea"
            />
          </div>

          {/* Photo Upload Section */}
          <div>
            <label className="block text-sm font-medium text-brand-brown mb-2">
              Photos (up to 3)
            </label>
            
            <div className="grid grid-cols-3 gap-3">
              {/* Uploaded Photos */}
              {formData.photos.map((photo, index) => (
                <div key={index} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                  <img 
                    src={photo.preview} 
                    alt={`Upload ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {/* Upload Button */}
              {formData.photos.length < 3 && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-brand-brown/30 hover:border-pastel-blue flex items-center justify-center cursor-pointer bg-pastel-pink/10 hover:bg-pastel-pink/20 transition-all">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <div className="text-center">
                    <Camera className="h-8 w-8 text-brand-brown/40 mx-auto mb-2" />
                    <p className="text-xs text-brand-brown/60">Add Photo</p>
                  </div>
                </label>
              )}
            </div>
          </div>

          {/* Business Information Section */}
          <div className="bg-pastel-blue/10 rounded-xl p-4 space-y-3">
            <div className="text-center border-b border-brand-brown/10 pb-3 mb-3">
              <p className="font-semibold text-brand-brown text-lg">
                {businessInfo.businessName}
              </p>
              <p className="text-sm text-brand-brown/70">
                Thank you for trusting us with your pet!
              </p>
            </div>

            {/* Contact Information */}
            <div className="space-y-2">
              {businessInfo.phone && (
                <div className="flex items-center space-x-2 text-sm text-brand-brown">
                  <Phone className="h-4 w-4 text-pastel-blue" />
                  <span>{businessInfo.phone}</span>
                </div>
              )}
              
              {businessInfo.website && (
                <div className="flex items-center space-x-2 text-sm text-brand-brown">
                  <Globe className="h-4 w-4 text-pastel-blue" />
                  <a href={businessInfo.website} target="_blank" rel="noopener noreferrer" className="hover:text-pastel-purple">
                    {businessInfo.website}
                  </a>
                </div>
              )}
            </div>

            {/* Social Media */}
            {(businessInfo.instagram || businessInfo.facebook || businessInfo.twitter) && (
              <div className="pt-2 border-t border-brand-brown/10">
                <p className="text-xs text-brand-brown/60 mb-2">Follow us:</p>
                <div className="flex items-center space-x-3">
                  {businessInfo.instagram && (
                    <a 
                      href={`https://instagram.com/${businessInfo.instagram}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-xs text-brand-brown hover:text-pastel-purple"
                    >
                      <Instagram className="h-4 w-4" />
                      <span>@{businessInfo.instagram}</span>
                    </a>
                  )}
                  
                  {businessInfo.facebook && (
                    <a 
                      href={businessInfo.facebook.startsWith('http') ? businessInfo.facebook : `https://${businessInfo.facebook}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-xs text-brand-brown hover:text-pastel-purple"
                    >
                      <Facebook className="h-4 w-4" />
                      <span>Facebook</span>
                    </a>
                  )}
                  
                  {businessInfo.twitter && (
                    <a 
                      href={`https://twitter.com/${businessInfo.twitter}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-xs text-brand-brown hover:text-pastel-purple"
                    >
                      <Twitter className="h-4 w-4" />
                      <span>@{businessInfo.twitter}</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Settings Reminder */}
            {!businessInfo.website && !businessInfo.phone && (
              <div className="text-center">
                <p className="text-xs text-brand-brown/50 italic">
                  Add your business info in Settings to customize this section
                </p>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary py-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn-primary py-3 flex items-center justify-center space-x-2"
            >
              <Send className="h-5 w-5" />
              <span>Send Summary</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VisitSummaryModal;
